import { useEffect, useRef, useState, useCallback } from 'react';

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  staggerDelay?: number;
  disableAnimation?: boolean;
}

export function useScrollReveal<T extends HTMLElement>({
  threshold = 0.15,
  rootMargin = '0px 0px -40px 0px',
  staggerDelay = 0,
  disableAnimation = false,
}: UseScrollRevealOptions = {}) {
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasAnimated = useRef(false);

  const checkMotion = useCallback(() => {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    if (!checkMotion() || disableAnimation) {
      setIsVisible(true);
      return;
    }

    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          const delay = staggerDelay > 0 ? staggerDelay : 0;
          if (delay > 0) {
            setTimeout(() => {
              setIsVisible(true);
              hasAnimated.current = true;
            }, delay);
          } else {
            setIsVisible(true);
            hasAnimated.current = true;
          }
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, staggerDelay, disableAnimation, checkMotion]);

  return { ref, isVisible };
}

export function getRevealClasses(isVisible: boolean): string {
  if (!isVisible) {
    return 'opacity-0 translate-y-[30px]';
  }
  return 'opacity-100 translate-y-0';
}

export function getRevealStyle(isVisible: boolean, delayMs = 0): React.CSSProperties {
  return {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
    transition: `opacity 600ms ease-out ${delayMs}ms, transform 600ms ease-out ${delayMs}ms`,
  };
}